import { useEffect, useState, useRef } from "react";
import {
  Typography,
  Box,
  alpha,
  useTheme,
  Stack,
  Button,
  Skeleton,
  LinearProgress,
} from "@mui/material";
import { Refresh, HistoryToggleOff } from "@mui/icons-material";
import { useDataFetch } from "../../core/hooks/useDataFetch";
import { useResponsive } from "../../core/hooks/useResponsive";
import { Config } from "../../config/config";
import { CashFlow } from "./components/CashFlow";
import { Holdings } from "./components/Holdings";
import { TradePerformance } from "./components/TradePerformance";
import { DashboardCharts } from "./components/DashboardCharts";
import SystemError from "../SystemError/systemError";
import ClosedTradeReviewDialog from "./components/ClosedTradeReviewDialog";

const Dashboard = () => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { callAPI } = useDataFetch();
  const { isPhone, isWide } = useResponsive();
  const callAPIRef = useRef(callAPI);
  callAPIRef.current = callAPI;
  const hasShownCelebrationRef = useRef(false);
  const [dashData, setDashData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  /**
   * Load dashboard once on mount + on explicit refresh only.
   * useDataFetch() may return a new `callAPI` each render; use a ref so the
   * mount effect stays [] without stale calls or an infinite re-fetch loop.
   * On failure, retry once automatically; if the retry also fails, stop on
   * SystemError instead of spinning on the skeleton forever.
   */
  async function loadDashboardData(isRetry = false) {
    setRefreshing(true);
    try {
      const res = await callAPIRef.current(Config.ITradeAPI.dashboard);
      if (res.status && res.data) {
        setDashData(res.data);
        setLoadFailed(false);
      } else if (!isRetry) {
        await loadDashboardData(true);
        return;
      } else {
        setLoadFailed(true);
      }
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    if (!dashData || hasShownCelebrationRef.current) return;
    const growthValue = Number.parseFloat(dashData?.cashFlow?.plnPercentage);
    if (growthValue > 0) {
      hasShownCelebrationRef.current = true;
      setShowCelebration(true);
      const timer = setTimeout(() => setShowCelebration(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [dashData]);

  if (loadFailed) {
    return (
      <SystemError
        title="Dashboard Load Failed"
        errorMessage="Could not load your dashboard data after retrying. Please try again."
      />
    );
  }

  if (dashData == null) {
    return (
      <Box sx={{ p: { xs: 1, md: 2 }, maxWidth: 1280, mx: "auto" }}>
        {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}
        <Skeleton variant="rounded" height={isPhone ? 96 : 120} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={isPhone ? 160 : 200} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={isPhone ? 240 : 320} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  // Section headings shrink a step on phones so they don't eat a third of the
  // viewport before any data shows.
  const headingVariant = isPhone ? "subtitle1" : "h6";

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: alpha(primary, 0.03),
        pb: 3,
        pt: { xs: 1, md: 1.5 },
        px: { xs: 0.75, sm: 1.5, md: 2 },
        position: "relative",
      }}
    >
      {refreshing && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            zIndex: 10,
            "& .MuiLinearProgress-bar": { transition: "transform 0.4s ease" },
          }}
        />
      )}

      <Box sx={{ maxWidth: 1400, mx: "auto" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1.5 }}>
          <Typography variant={headingVariant} fontWeight={800} sx={{ letterSpacing: "-0.01em" }} noWrap>
            Capital &amp; exposure
          </Typography>
          <Button
            variant="contained"
            size={isPhone ? "small" : "medium"}
            startIcon={<Refresh />}
            onClick={() => loadDashboardData()}
            disabled={refreshing}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 800,
              px: { xs: 1.25, sm: 2 },
              minWidth: { xs: "auto", sm: 112 },
              flexShrink: 0,
              boxShadow: `0 6px 20px ${alpha(primary, 0.25)}`,
            }}
          >
            Refresh
          </Button>
        </Stack>

        {/* The analytics side rail only earns its keep at >=1440px. Below that the
            charts drop under the main column and lay out side by side (>=768) or
            collapse into a tab strip (<768) instead of stacking full-height. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", xl: "4fr 1fr" },
            gap: { xs: 1.5, md: 2 },
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ mb: { xs: 1.75, md: 2.25 } }}>
              <CashFlow data={dashData?.cashFlow} />
            </Box>

            <Typography variant={headingVariant} fontWeight={800} sx={{ letterSpacing: "-0.01em", mb: 1 }}>
              Live holdings
            </Typography>
            <Holdings data={dashData?.holdings || []} onRefresh={() => loadDashboardData()} />

            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              gap={1.25}
              sx={{ mt: 2, mb: 1 }}
            >
              <Typography variant={headingVariant} fontWeight={800} sx={{ letterSpacing: "-0.01em" }}>
                Closed trades
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<HistoryToggleOff />}
                onClick={() => setReviewOpen(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  px: { xs: 1.25, sm: 2 },
                  flexShrink: 0,
                  boxShadow: `0 6px 20px ${alpha(primary, 0.25)}`,
                }}
              >
                {isPhone ? "Post-exit" : "Post-exit review"}
              </Button>
            </Stack>
            {dashData?.tradePerformance?.message && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {dashData.tradePerformance.message}
              </Typography>
            )}
            <TradePerformance data={dashData?.tradePerformance} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={headingVariant}
              fontWeight={800}
              sx={{ letterSpacing: "-0.01em", mb: 1, mt: { xs: 2, xl: 0 } }}
            >
              Analytics
            </Typography>
            <DashboardCharts
              cashFlow={dashData?.cashFlow}
              holdings={dashData?.holdings || []}
              tradePerformance={dashData?.tradePerformance}
              singleColumn={isWide}
              compact
              variant="holdings-side"
            />
          </Box>
        </Box>
      </Box>

      <ClosedTradeReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} />

      {showCelebration && (
        <Box
          sx={{
            position: "fixed",
            top: { xs: 70, md: 84 },
            right: { xs: 12, md: 24 },
            zIndex: 1200,
            pointerEvents: "none",
            animation: "celebratePop 1s ease forwards",
            "@keyframes celebratePop": {
              "0%": { opacity: 0, transform: "translateY(8px) scale(0.9)" },
              "20%": { opacity: 1, transform: "translateY(0) scale(1)" },
              "80%": { opacity: 1, transform: "translateY(0) scale(1)" },
              "100%": { opacity: 0, transform: "translateY(-8px) scale(1.02)" },
            },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "1.15rem", md: "1.75rem" },
              fontWeight: 800,
              px: 1.25,
              py: 0.5,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: theme.palette.success.main,
              boxShadow: `0 8px 22px ${alpha(theme.palette.success.main, 0.22)}`,
            }}
          >
            🎉 Great start!
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
